/**
 * emitTouchpoint — the add-in's module-level touchpoint BRIDGE (story 26747,
 * post-merge wiring P2.11).
 *
 * This used to be a shim that recorded nothing (see git history). It now
 * forwards to whichever `TouchpointProvider` is mounted at the add-in root
 * (`main.tsx`), via the provider's module-level bridge
 * (`getActiveTouchpointEmit`, in `@product-app/common/workflows/telemetry/useTouchpoint`).
 *
 * Kept as a plain function — not converted to `useTouchpoint()` at each call
 * site — on purpose: every existing call site was written against this
 * function's loose `(kind, phase, fields?: Record<string, unknown>)` shape
 * before the real wire contract existed (several are inside callbacks/
 * cleanup functions, not at component top-level), and `App.test.tsx` pins
 * those call sites by mocking this module directly
 * (`jest.mock("./obs/touchpoints", ...)`) — switching to the hook would
 * require rewriting both the call sites and that test. This bridge lets the
 * call sites stay put.
 *
 * Field mapping: the real wire event (`TouchpointEvent`) has no room for
 * this file's original ad hoc fields (`workflow_id`, `instance_id`, `view`,
 * `corrections_count`, ...) — only `subject_type`/`subject_id` survive.
 * `deriveSubject` recovers them: an `instance_id` field means the touchpoint
 * is about a RUN; a `workflow_id` field (no `instance_id` yet — the brief
 * hasn't launched) means it's about the BRIEF. Any other/extra fields are
 * dropped; a event with neither is dropped entirely (nothing to attach it
 * to) rather than posted with an empty subject.
 */
import {
  getActiveTouchpointEmit,
} from "@product-app/common/workflows/telemetry/useTouchpoint";
import {
  TOUCHPOINT_KINDS,
  type TouchpointKind,
  type TouchpointPhase,
} from "@product-app/common/workflows/telemetry/touchpoints";

function deriveSubject(
  fields?: Record<string, unknown>,
): { subjectType: "run" | "brief"; subjectId: string } | null {
  const instanceId = fields?.instance_id;
  if (typeof instanceId === "string" && instanceId.length > 0) {
    return { subjectType: "run", subjectId: instanceId };
  }
  const workflowId = fields?.workflow_id;
  if (typeof workflowId === "string" && workflowId.length > 0) {
    return { subjectType: "brief", subjectId: workflowId };
  }
  return null;
}

export function emitTouchpoint(
  kind: string,
  phase: TouchpointPhase,
  fields?: Record<string, unknown>,
): void {
  try {
    if (!TOUCHPOINT_KINDS.includes(kind as TouchpointKind)) return;
    const emit = getActiveTouchpointEmit();
    if (!emit) return; // no TouchpointProvider mounted yet — safe no-op.
    const subject = deriveSubject(fields);
    if (!subject) return;
    emit(kind as TouchpointKind, phase, {
      subject_type: subject.subjectType,
      subject_id: subject.subjectId,
      surface: "outlook_addin",
    });
  } catch {
    // Telemetry must never break the caller.
  }
}

export default emitTouchpoint;
